import { usePageBreadcrumbs } from "@/components/layout/breadcrumb-context";
import { RecipeCard } from "@/components/recipes/RecipeCard";
import { RecipeCardList } from "@/components/recipes/RecipeCardList";
import { Skeleton } from "@/components/ui/skeleton";
import { ViewToggle } from "@/components/ui/view-toggle";
import { useAllRecipes } from "@/hooks/use-all-recipes";
import { useAuthProfile } from "@/hooks/use-auth-profile";
import { useRecipeBoxes } from "@/hooks/use-recipe-boxes";
import { useViewPreference } from "@/hooks/use-view-preference";
import { Star } from "lucide-react";
import { useMemo } from "react";


export default function FavoritesPage() {
  const { user } = useAuthProfile();
  const { boxes: recipeBoxes, loading: recipeBoxesLoading } = useRecipeBoxes(user?.uid);
  const { recipes: allRecipes, loading: recipesLoading, error: recipesError } = useAllRecipes(recipeBoxes);
  const { viewPreference, setViewPreference } = useViewPreference();

  usePageBreadcrumbs([{ label: "Recipe Boxes", href: "/home" }, { label: "Favorites" }]);

  // Filter to only show favorite recipes from the user's accessible recipe boxes
  const favoriteRecipes = useMemo(() => {
    return allRecipes.filter((recipe) => recipe.isFavorite === true);
  }, [allRecipes]);

  const loading = recipeBoxesLoading || recipesLoading;
  const error = recipesError;

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center gap-2">
          <h1 className="font-headline text-2xl font-semibold">Favorites</h1>
        </div>
        {viewPreference === "card" ? (
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Skeleton key={i} className="aspect-square w-full rounded-lg" />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-lg" />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-2">
        <h1 className="font-headline text-2xl font-semibold">Favorites</h1>
        <div className="ml-auto">
          <ViewToggle view={viewPreference} onViewChange={setViewPreference} />
        </div>
      </div>

      {favoriteRecipes.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-center">
          <Star className="mx-auto h-12 w-12 text-muted-foreground" />
          <p className="mt-4 font-headline text-lg">No favorite recipes yet</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Star recipes you love to find them quickly here.
          </p>
        </div>
      ) : viewPreference === "card" ? (
        <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(9rem, 1fr))" }}>
          {favoriteRecipes.map((recipe) => (
            <RecipeCard
              key={recipe.id}
              recipe={recipe}
              userId={user?.uid ?? null}
              onFavoriteToggle={(isFavorite) => {
                // When a recipe is unfavorited, it will automatically disappear
                // from the list because useAllRecipes will update from Firestore
                if (!isFavorite) {
                  // Recipe was unfavorited - it will be removed from the list automatically
                }
              }}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {favoriteRecipes.map((recipe) => (
            <RecipeCardList
              key={recipe.id}
              recipe={recipe}
              userId={user?.uid ?? null}
              onFavoriteToggle={(isFavorite) => {
                // When a recipe is unfavorited, it will automatically disappear
                // from the list because useAllRecipes will update from Firestore
                if (!isFavorite) {
                  // Recipe was unfavorited - it will be removed from the list automatically
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

